import { useNavigate } from "react-router-dom";
import { ChevronsUpDown, CreditCard, LogOut, Settings } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import { cn } from "@/lib/utils";

export function Avatar({ me, size = 32, className }) {
  return (
    <span
      className={cn("flex shrink-0 items-center justify-center rounded-full border border-white/10 bg-ink-750 font-medium text-zinc-200", className)}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      data-testid="user-avatar"
    >
      {initials(me?.profile?.display_name, me?.email)}
    </span>
  );
}

function MenuBody({ me }) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  return (
    <DropdownMenuContent align="end" className="w-60 border-white/10 bg-ink-850 p-1.5 text-zinc-200">
      <DropdownMenuLabel className="px-2 py-1.5 font-normal">
        <div className="truncate text-sm font-medium text-white">{me?.profile?.display_name || "Your account"}</div>
        <div className="truncate text-xs text-zinc-500" data-testid="account-menu-email">{me?.email}</div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator className="bg-white/[0.07]" />
      <DropdownMenuItem onSelect={() => navigate("/plan")} data-testid="account-menu-plan" className="gap-2.5 rounded-md focus:bg-white/[0.06]">
        <CreditCard className="h-4 w-4" strokeWidth={1.6} /> Plan & usage
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => navigate("/settings")} data-testid="account-menu-settings" className="gap-2.5 rounded-md focus:bg-white/[0.06]">
        <Settings className="h-4 w-4" strokeWidth={1.6} /> Settings
      </DropdownMenuItem>
      <DropdownMenuSeparator className="bg-white/[0.07]" />
      <DropdownMenuItem
        onSelect={async () => {
          await signOut();
          navigate("/sign-in", { replace: true });
        }}
        data-testid="account-menu-logout"
        className="gap-2.5 rounded-md focus:bg-white/[0.06]"
      >
        <LogOut className="h-4 w-4" strokeWidth={1.6} /> Log out
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

export function AccountButton({ me }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="sidebar-account-button"
          className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-white/[0.04]"
        >
          <Avatar me={me} size={34} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-zinc-100">{me?.profile?.display_name || "My account"}</span>
            <span className="block truncate text-[11.5px] text-zinc-500">{me?.email || "View profile"}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 text-zinc-600" />
        </button>
      </DropdownMenuTrigger>
      <MenuBody me={me} />
    </DropdownMenu>
  );
}

export function AccountMenu({ me }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Account menu" data-testid="user-account-menu" className="rounded-full transition-opacity hover:opacity-85">
          <Avatar me={me} size={32} />
        </button>
      </DropdownMenuTrigger>
      <MenuBody me={me} />
    </DropdownMenu>
  );
}
